import axios from '../../../configs/axios';

// Types for Deal API
export interface Deal {
  uuid: string;
  companyName: string;
  vatNumber: string;
  contactPerson: string;
  region: string;
  note?: string;
  services?: string[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface CreateDealRequest {
  companyName: string;
  vatNumber: string;
  contactPerson: string;
  region: string;
  note?: string;
  services?: string[];
}

export interface UpdateDealRequest {
  companyName?: string;
  vatNumber?: string;
  contactPerson?: string;
  region?: string;
  note?: string;
  services?: string[];
}

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

export interface VatCheckResponse {
  // Whether the VAT ID is formally valid (Partita IVA checksum). Absent on
  // older responses; treat undefined as valid for backward compatibility.
  valid?: boolean;
  exists: boolean;
}

// API Functions
export const dealApi = {
  // Get all deals (optionally filtered by company VAT number)
  getAllDeals: async (vatNumber?: string): Promise<ApiResponse<Deal[]>> => {
    const params = vatNumber ? { vatNumber } : {};
    const response = await axios.get('/deals', { params });
    return response.data;
  },

  // Create new deal
  createDeal: async (data: CreateDealRequest): Promise<ApiResponse<Deal>> => {
    const response = await axios.post('/deals', data);
    return response.data;
  },

  // Get deal by UUID
  getDealByUuid: async (uuid: string): Promise<ApiResponse<Deal>> => {
    const response = await axios.get(`/deals/${uuid}`);
    return response.data;
  },

  // Update deal
  updateDeal: async (uuid: string, data: UpdateDealRequest): Promise<ApiResponse<Deal>> => {
    const response = await axios.put(`/deals/${uuid}`, data);
    return response.data;
  },

  // Delete deal
  deleteDeal: async (uuid: string): Promise<ApiResponse<void>> => {
    const response = await axios.delete(`/deals/${uuid}`);
    return response.data;
  },

  // Check if VAT number exists
  checkVatExists: async (vatNumber: string): Promise<ApiResponse<VatCheckResponse>> => {
    const response = await axios.get(`/deals/check-vat?vatNumber=${vatNumber}`);
    return response.data;
  },
};

// Export individual functions for easier imports
export const {
  getAllDeals,
  createDeal,
  getDealByUuid,
  updateDeal,
  deleteDeal,
  checkVatExists,
} = dealApi;